import { classifyLinkImpairment } from '../linkImpairment';
import type { PlaybackCapacityDecision, PlaybackCapacityEvidence } from '../streamRecovery/qualityContracts';
import { CONTINUOUS_LOW_FPS_REQUIRED_MS, POOR_PLAYBACK_BITRATE_HOLD_MS, POOR_PLAYBACK_REQUIRED_MS, POOR_PLAYBACK_WINDOW_MS, VISIBLE_STALL_KEYFRAME_MS } from '../streamRecovery/policy';
import { isPlaybackCapacityEvidence } from '../streamRecovery/validators';

type Interval = { start: number; end: number };

/** Windowed playback policy; observes evidence but never changes encoding itself. */
export class SustainedPoorPlaybackPolicy {
  private intervals: Interval[] = [];
  private previous?: PlaybackCapacityEvidence;
  private lastCapacityAt?: number;
  private episodeId?: string;
  private requested = false;
  private continuousLowFpsMs = 0;
  private lowFpsEndedAt?: number;
  private continuousSelfMs = 0;
  private selfEndedAt?: number;

  constructor(private readonly options: { newEpisodeId: () => string }) {}

  observe(sample: PlaybackCapacityEvidence): PlaybackCapacityDecision {
    if (!isPlaybackCapacityEvidence(sample) || sample.intervalMs > 3_000) {
      this.clearEpisode();
      return this.decision('unknown', 0, 'invalid-interval');
    }
    if (this.previous && sample.peerGeneration === this.previous.peerGeneration
      && sample.pathEpoch === this.previous.pathEpoch && sample.sequence > this.previous.sequence
      && sample.sampledAtMs <= this.previous.sampledAtMs) {
      this.clearEpisode();
      return this.decision('unknown', 0, 'clock-discontinuity');
    }
    if (this.isOld(sample)) return this.decision('unknown', 0, 'duplicate-or-old-sample');
    if (this.previous && (sample.peerGeneration !== this.previous.peerGeneration || sample.pathEpoch !== this.previous.pathEpoch)) this.reset();
    const previousAt = this.previous?.sampledAtMs;
    if (previousAt !== undefined && sample.sampledAtMs - previousAt >= 3_000) this.clearEpisode();
    this.previous = sample;
    if (sample.visibility === 'hidden' || sample.playback === 'paused' || sample.playback === 'ended') {
      this.clearEpisode();
      return this.decision('suspended', 0, 'not-visible-playing');
    }
    if (sample.freshness.state !== 'fresh' || (sample.freshness.ageMs ?? 0) >= 3_000 || sample.visibility !== 'visible' || sample.playback !== 'playing') {
      this.clearEpisode();
      return this.decision('unknown', 0, 'unavailable-playback');
    }
    return this.evaluate(sample, previousAt);
  }

  reset(): void {
    this.previous = undefined;
    this.clearEpisode();
  }

  private evaluate(sample: PlaybackCapacityEvidence, previousAt: number | undefined): PlaybackCapacityDecision {
    const now = sample.sampledAtMs;
    const activeShortage = sample.sourceActive === true && sample.presentedFps !== undefined
      && sample.presentedFps < Math.min(20, sample.configuredFps * 0.5);
    const freeze = (sample.freezeDelta ?? 0) >= 1 && (sample.freezeDurationDeltaMs ?? 0) >= 150;
    const ongoingStall = sample.sourceActive !== false
      && (sample.ongoingPresentationStallMs ?? 0) >= VISIBLE_STALL_KEYFRAME_MS;
    const trouble = activeShortage || freeze || ongoingStall;
    const start = Math.max(now - sample.intervalMs, previousAt ?? 0);
    const selfHold = activeShortage || ongoingStall;
    this.continuousSelfMs = selfHold && now > start
      ? now - start + (this.selfEndedAt === start ? this.continuousSelfMs : 0) : 0;
    this.selfEndedAt = this.continuousSelfMs > 0 ? now : undefined;
    // A presentation shortage can request a soft cut on its own. It does not
    // corroborate capacity, so it cannot unlock a tier demotion.
    if (this.capacityTrouble(sample)) this.lastCapacityAt = now;
    const corroborated = this.lastCapacityAt !== undefined && now - this.lastCapacityAt <= 3_000;
    this.continuousLowFpsMs = activeShortage && corroborated && now > start
      ? now - start + (this.lowFpsEndedAt === start ? this.continuousLowFpsMs : 0) : 0;
    this.lowFpsEndedAt = this.continuousLowFpsMs > 0 ? now : undefined;
    if (trouble && corroborated && now > start) this.intervals.push({ start, end: now });
    this.intervals = this.intervals.filter(interval => interval.end > now - POOR_PLAYBACK_WINDOW_MS);
    const qualifyingMs = this.intervals.reduce((sum, interval) => sum + interval.end - Math.max(interval.start, now - POOR_PLAYBACK_WINDOW_MS), 0);
    if (!trouble) {
      const demonstratedProgress = sample.presentedFps !== undefined && sample.presentedFps >= Math.min(20, sample.configuredFps * 0.5);
      const knownIdle = sample.sourceActive === false && sample.freezeDelta === 0;
      if (!demonstratedProgress && !knownIdle) return this.decision('unknown', qualifyingMs, 'playback-progress-unproven');
      if (this.requested) {
        this.clearEpisode();
        return this.decision('healthy', 0, 'playback-progress');
      }
      return this.decision('healthy', qualifyingMs, 'playback-progress');
    }
    if (!corroborated) return this.protectPresentation(sample, qualifyingMs);
    if (!this.episodeId) this.episodeId = this.options.newEpisodeId();
    if (qualifyingMs < POOR_PLAYBACK_REQUIRED_MS && this.continuousLowFpsMs <= CONTINUOUS_LOW_FPS_REQUIRED_MS)
      return this.finish(sample, 'qualifying', qualifyingMs, 'collecting-poor-playback');
    if (sample.currentTier === 'bottom') {
      this.requested = true;
      return this.finish(sample, 'at-bottom', qualifyingMs, 'poor-playback-at-bottom');
    }
    if (!sample.bitrateProtectionApplied) return this.finish(sample, 'qualifying', qualifyingMs, 'awaiting-bitrate');
    if (this.requested) return this.finish(sample, 'qualifying', qualifyingMs, 'demotion-requested');
    this.requested = true;
    return this.finish(sample, 'demote', qualifyingMs, 'sustained-poor-playback', { requestTier: 'bottom' });
  }

  private capacityTrouble(sample: PlaybackCapacityEvidence): boolean {
    // Wireless loss cannot corroborate capacity trouble on its own.
    const wirelessLoss = classifyLinkImpairment({
      packetsLostPct: sample.lossPct ?? 0,
      standingQueueMs: sample.standingQueueMs ?? null,
    }) === 'wireless-loss';
    const queue = sample.standingQueueMs;
    const queueKnown = typeof queue === 'number' && Number.isFinite(queue);
    // Loss before the floor exists has no queue to blame. A 7 ms trip with 15% loss
    // was hard-cut on that absence and stayed at the 480p minimum.
    if (queueKnown && ((!wirelessLoss && (sample.lossPct ?? 0) >= 2) || queue >= 100)) return true;
    if (!sample.hostEvidenceFresh) return false;
    if (sample.bandwidthLimited === true) return true;
    // Fresh host capacity evidence still counts when wireless loss is also present.
    const sent = sample.senderSentBitrateBps;
    return sample.sourceActive === true && sample.receivedVideoBitrateBps !== undefined
      && sent !== undefined && Number.isFinite(sent) && sent > 0
      && sample.receivedVideoBitrateBps / sent < 0.7;
  }

  private protectionStrength(sample: PlaybackCapacityEvidence): 'soft' | 'hard' {
    const wirelessLoss = classifyLinkImpairment({
      packetsLostPct: sample.lossPct ?? 0,
      standingQueueMs: sample.standingQueueMs ?? null,
    }) === 'wireless-loss';
    if (wirelessLoss) return 'soft';
    const queue = sample.standingQueueMs;
    if (typeof queue === 'number' && Number.isFinite(queue) && queue >= 100) return 'hard';
    if (sample.bandwidthLimited === true) return 'hard';
    if ((sample.lossPct ?? 0) >= 2) return 'hard';
    const sent = sample.senderSentBitrateBps;
    const received = sample.receivedVideoBitrateBps;
    if (sent !== undefined && Number.isFinite(sent) && sent > 0 && received !== undefined && received / sent < 0.7) return 'hard';
    return 'soft';
  }

  /** Soft-only. There is no queue, loss, or sent-rate proof behind this hold. */
  private protectPresentation(sample: PlaybackCapacityEvidence, qualifyingMs: number): PlaybackCapacityDecision {
    const decision = this.decision('unknown', qualifyingMs, 'capacity-uncorroborated');
    if (sample.bitrateProtectionApplied || this.continuousSelfMs < POOR_PLAYBACK_BITRATE_HOLD_MS) return decision;
    return { ...decision, protectBitrate: 'soft' };
  }

  private finish(
    sample: PlaybackCapacityEvidence,
    state: PlaybackCapacityDecision['state'],
    qualifyingMs: number,
    reason: string,
    extra?: Partial<PlaybackCapacityDecision>,
  ): PlaybackCapacityDecision {
    const decision = { ...this.decision(state, qualifyingMs, reason), ...extra };
    if (sample.bitrateProtectionApplied) return decision;
    const held = qualifyingMs >= POOR_PLAYBACK_BITRATE_HOLD_MS || this.continuousSelfMs >= POOR_PLAYBACK_BITRATE_HOLD_MS;
    if (!held) return decision;
    return { ...decision, protectBitrate: this.protectionStrength(sample) };
  }

  private isOld(sample: PlaybackCapacityEvidence): boolean {
    const previous = this.previous;
    if (!previous) return false;
    if (sample.peerGeneration < previous.peerGeneration) return true;
    if (sample.peerGeneration > previous.peerGeneration) return false;
    if (sample.pathEpoch < previous.pathEpoch) return true;
    if (sample.pathEpoch > previous.pathEpoch) return false;
    return sample.sequence <= previous.sequence || sample.sampledAtMs <= previous.sampledAtMs;
  }

  private clearEpisode(): void {
    this.intervals = [];
    this.continuousLowFpsMs = 0;
    this.lowFpsEndedAt = undefined;
    this.continuousSelfMs = 0;
    this.selfEndedAt = undefined;
    this.lastCapacityAt = undefined;
    this.episodeId = undefined;
    this.requested = false;
  }

  private decision(state: PlaybackCapacityDecision['state'], qualifyingMs: number, reason: string): PlaybackCapacityDecision {
    return { state, qualifyingMs, reason, ...(this.episodeId ? { episodeId: this.episodeId } : {}) };
  }
}
